/**
 * Outstanding — Enterprise-grade receivables tracker.
 *
 * Refactored as a premium SaaS-CRM view: sticky filter toolbar, quick chips,
 * clickable summary buckets, patient-detail side drawer, client-side
 * pagination/sorting/column-customisation (backend contract unchanged), and
 * bulk actions.
 *
 * The backend `/outstanding` endpoint stays untouched — this file only
 * reshapes what we already receive. When the API is upgraded to serve
 * paginated pages, the `paged` memo below is the single point that needs to
 * be swapped for a server-driven fetcher.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  Search, Filter, MessageCircle, Banknote, Loader2, Wallet, Receipt,
  Users, TrendingDown, ArrowUpRight, Send, MoreVertical, Download,
  Printer, RefreshCw, ChevronLeft, ChevronRight, Columns3, ChevronDown,
  ChevronUp, X, Mail, MessageSquare, Bookmark, FileSpreadsheet, FileText,
  Stethoscope, Sparkles, Check, Clock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import PatientAvatar from "@/components/PatientAvatar";
import CollectPaymentDialog from "@/components/app/CollectPaymentDialog";
import BillingTabs from "@/components/app/BillingTabs";
import OutstandingDrawer from "@/components/billing/OutstandingDrawer";
import { DateDMYInput } from "@/components/DateDMYInput";

// ---- helpers ----------------------------------------------------------------
const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 0,
})}`;

const BUCKETS = [
  { key: "0-30",    label: "0–30 days",    tone: "bg-emerald-50 border-emerald-200 text-emerald-800",
    pillTone: "bg-emerald-100 text-emerald-800" },
  { key: "31-60",   label: "31–60 days",   tone: "bg-amber-50 border-amber-200 text-amber-800",
    pillTone: "bg-amber-100 text-amber-800" },
  { key: "61-90",   label: "61–90 days",   tone: "bg-orange-50 border-orange-200 text-orange-800",
    pillTone: "bg-orange-100 text-orange-800" },
  { key: "91-180",  label: "91–180 days",  tone: "bg-red-50 border-red-200 text-red-800",
    pillTone: "bg-red-100 text-red-800" },
  { key: "181-365", label: "181–365 days", tone: "bg-rose-50 border-rose-200 text-rose-800",
    pillTone: "bg-rose-100 text-rose-800" },
  { key: "365+",    label: "365+ days",    tone: "bg-purple-50 border-purple-200 text-purple-800",
    pillTone: "bg-purple-100 text-purple-800" },
];

const CHIPS = [
  { key: "all",        label: "All" },
  { key: "0-30",       label: "0–30 d" },
  { key: "31-60",      label: "31–60 d" },
  { key: "61-90",      label: "61–90 d" },
  { key: "91-180",     label: "91–180 d" },
  { key: "365+",       label: "365+ d" },
  { key: "high",       label: "High >₹10,000" },
  { key: "no-reminder",label: "No reminder sent" },
  { key: "reminder-failed", label: "Reminder failed" },
  { key: "wallet",     label: "Wallet available" },
  { key: "overdue-today", label: "Overdue today" },
];

// Column configuration — order preserved; `optional=true` columns can be
// toggled by the user via the "Columns" menu.
const DEFAULT_COLUMNS = [
  { key: "patient",   label: "Patient",         optional: false, sortable: true, sortKey: "name" },
  { key: "doctor",    label: "Doctor",          optional: true,  sortable: true, sortKey: "primary_doctor_id" },
  { key: "outstanding", label: "Outstanding",   optional: false, sortable: true, sortKey: "outstanding" },
  { key: "age",       label: "Age",             optional: false, sortable: true, sortKey: "days_overdue" },
  { key: "wallet",    label: "Wallet",          optional: true,  sortable: true, sortKey: "wallet_balance" },
  { key: "lastpay",   label: "Last payment",    optional: true,  sortable: true, sortKey: "last_payment_at" },
  { key: "reminder",  label: "Reminder status", optional: true,  sortable: false },
  { key: "actions",   label: "Actions",         optional: false, sortable: false },
];

const LS = {
  PAGE_SIZE:  "viyora.outstanding.pageSize",
  COLS:       "viyora.outstanding.columns",
};

// ---- Reminder-status derivation --------------------------------------------
// The current API doesn't yet return per-row reminder status. When it does,
// swap this helper for a straight `row.reminder_status` read.
const reminderStatus = (row) => {
  if (row.reminder_status) return row.reminder_status;
  if (row.last_reminder_at) return "sent";
  return "never";
};

const REMINDER_TONES = {
  sent:      "bg-sky-50 text-sky-800 border-sky-200",
  delivered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  read:      "bg-pine/10 text-pine border-pine/20",
  failed:    "bg-red-50 text-red-800 border-red-200",
  never:     "bg-linen text-ink-mid border-[#E8EAE9]",
};
const REMINDER_LABEL = { sent: "Sent", delivered: "Delivered", read: "Read", failed: "Failed", never: "Never sent" };

// ---- Filter defaults --------------------------------------------------------
const emptyFilters = () => ({
  q: "",
  doctorId: "all",
  bucket: "all",
  amountMin: "",
  amountMax: "",
  reminder: "all",   // all | sent | delivered | read | failed | never
  wallet: "all",     // all | has | none
  lastPayBefore: "", // YYYY-MM-DD
});

export default function Outstanding() {
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState([]);

  // Filter state (applied) + draft state (for pending changes in "More filters")
  const [filters, setFilters] = useState(emptyFilters);
  const [draft, setDraft]   = useState(emptyFilters);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [chip, setChip] = useState("all");

  // Sort
  const [sortKey, setSortKey] = useState("days_overdue");
  const [sortDir, setSortDir] = useState("desc");

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    Number(localStorage.getItem(LS.PAGE_SIZE)) || 25
  );

  // Columns
  const [cols, setCols] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(LS.COLS) || "null");
      if (!raw) return DEFAULT_COLUMNS.map((c) => c.key);
      // Keep only known columns, in the default order
      const set = new Set(raw);
      return DEFAULT_COLUMNS.filter((c) => !c.optional || set.has(c.key)).map((c) => c.key);
    } catch { return DEFAULT_COLUMNS.map((c) => c.key); }
  });

  // Selection
  const [selected, setSelected] = useState(new Set());

  // Actions
  const [collectFor, setCollectFor] = useState(null);
  const [drawerRow, setDrawerRow] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // ---- Data load ------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/outstanding");
      setRawData(data);
    } catch {
      toast.error("Could not load outstanding");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data || [])).catch(() => setTeam([]));
  }, []);

  // Persist rows-per-page + column choices
  useEffect(() => { localStorage.setItem(LS.PAGE_SIZE, String(pageSize)); }, [pageSize]);
  useEffect(() => {
    const optionalCols = cols.filter((k) => DEFAULT_COLUMNS.find((c) => c.key === k && c.optional));
    localStorage.setItem(LS.COLS, JSON.stringify(optionalCols));
  }, [cols]);

  // Reset page when filters/chip/sort change
  useEffect(() => { setPage(1); setSelected(new Set()); }, [filters, chip, sortKey, sortDir]);

  // ---- Filtering pipeline ---------------------------------------------------
  const doctorMap = useMemo(() => {
    const m = {};
    for (const t of team) if (t.role === "doctor" || t.role === "admin") {
      m[t.id] = /^dr\.?\s+/i.test(t.name || "") ? t.name : `Dr. ${t.name || "Doctor"}`;
    }
    return m;
  }, [team]);

  const filtered = useMemo(() => {
    const rows = rawData?.rows || [];
    const q = (filters.q || "").trim().toLowerCase();
    const minA = filters.amountMin ? Number(filters.amountMin) : null;
    const maxA = filters.amountMax ? Number(filters.amountMax) : null;
    const lastBefore = filters.lastPayBefore ? new Date(filters.lastPayBefore) : null;
    return rows.filter((r) => {
      if (q && !((r.name || "").toLowerCase().includes(q) || (r.phone || "").includes(q))) return false;
      if (filters.doctorId !== "all") {
        const did = r.primary_doctor_id || r.assigned_doctor_id;
        if (filters.doctorId === "unassigned" ? did : did !== filters.doctorId) return false;
      }
      if (filters.bucket !== "all" && r.bucket !== filters.bucket) return false;
      if (minA != null && r.outstanding < minA) return false;
      if (maxA != null && r.outstanding > maxA) return false;
      if (filters.reminder !== "all" && reminderStatus(r) !== filters.reminder) return false;
      if (filters.wallet === "has" && !(r.wallet_balance > 0)) return false;
      if (filters.wallet === "none" && r.wallet_balance > 0) return false;
      if (lastBefore) {
        if (!r.last_payment_at) return true;
        if (new Date(r.last_payment_at) >= lastBefore) return false;
      }
      // Chip filters
      switch (chip) {
        case "all": break;
        case "0-30": case "31-60": case "61-90": case "91-180": case "365+":
          if (r.bucket !== chip) return false; break;
        case "high":            if (!(r.outstanding > 10000)) return false; break;
        case "no-reminder":     if (reminderStatus(r) !== "never") return false; break;
        case "reminder-failed": if (reminderStatus(r) !== "failed") return false; break;
        case "wallet":          if (!(r.wallet_balance > 0)) return false; break;
        case "overdue-today":   if (r.days_overdue !== 0) return false; break;
        default: break;
      }
      return true;
    });
  }, [rawData, filters, chip]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const paged = sorted.slice(pageStart, pageStart + pageSize);

  const applyFilters = () => setFilters(draft);
  const resetFilters = () => { setFilters(emptyFilters()); setDraft(emptyFilters()); setChip("all"); setAdvancedOpen(false); };
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.q) n++;
    if (filters.doctorId !== "all") n++;
    if (filters.bucket !== "all") n++;
    if (filters.amountMin || filters.amountMax) n++;
    if (filters.reminder !== "all") n++;
    if (filters.wallet !== "all") n++;
    if (filters.lastPayBefore) n++;
    return n;
  }, [filters]);

  // ---- Sort helpers ---------------------------------------------------------
  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };
  const SortIcon = ({ col }) => {
    if (!col.sortable) return null;
    if (sortKey !== col.sortKey) return <ChevronDown className="inline w-3 h-3 opacity-30 ml-0.5" />;
    return sortDir === "asc"
      ? <ChevronUp className="inline w-3 h-3 opacity-80 ml-0.5" />
      : <ChevronDown className="inline w-3 h-3 opacity-80 ml-0.5" />;
  };

  // ---- Selection ------------------------------------------------------------
  const allOnPageSelected = paged.length > 0 && paged.every((r) => selected.has(r.patient_id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) paged.forEach((r) => next.delete(r.patient_id));
      else paged.forEach((r) => next.add(r.patient_id));
      return next;
    });
  };
  const toggleOne = (pid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid); else next.add(pid);
      return next;
    });
  };

  // ---- Bulk / Export --------------------------------------------------------
  const selectedRows = useMemo(() => sorted.filter((r) => selected.has(r.patient_id)), [sorted, selected]);

  const bulkRemind = async () => {
    if (!selectedRows.length) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const r of selectedRows) {
      try { await api.post(`/outstanding/${r.patient_id}/remind`); ok++; }
      catch { fail++; }
    }
    setBulkBusy(false);
    if (ok) toast.success(`Reminders sent to ${ok} patient${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}`);
    else toast.error("Failed to send reminders");
    setSelected(new Set());
  };

  const notImplemented = (label) => toast.info(`${label} — coming soon in the enterprise messaging module.`);

  const exportRows = (scope, format) => {
    let rows = [];
    if (scope === "page")    rows = paged;
    else if (scope === "filtered") rows = sorted;
    else rows = rawData?.rows || [];

    if (!rows.length) return toast.error("Nothing to export.");

    if (format === "pdf") { window.print(); return; }

    const cols = [
      ["Patient", (r) => r.name],
      ["Phone", (r) => r.phone || ""],
      ["Doctor", (r) => doctorMap[r.primary_doctor_id || r.assigned_doctor_id] || "Unassigned"],
      ["Outstanding", (r) => r.outstanding],
      ["Age (days)", (r) => r.days_overdue],
      ["Bucket", (r) => r.bucket],
      ["Invoices", (r) => (r.invoices || []).length],
      ["Wallet", (r) => r.wallet_balance || 0],
      ["Last payment", (r) => r.last_payment_at || ""],
      ["Last payment amount", (r) => r.last_payment_amount || ""],
      ["Reminder", (r) => REMINDER_LABEL[reminderStatus(r)]],
    ];
    const escape = (v) => {
      const s = String(v ?? "");
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [
      cols.map(([h]) => escape(h)).join(","),
      ...rows.map((r) => cols.map(([, f]) => escape(f(r))).join(",")),
    ];
    const mime = format === "xlsx" ? "application/vnd.ms-excel" : "text/csv";
    const ext = format === "xlsx" ? "xls" : "csv";
    const blob = new Blob([lines.join("\n")], { type: mime + ";charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `outstanding-${scope}-${new Date().toISOString().slice(0, 10)}.${ext}`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row${rows.length === 1 ? "" : "s"}`);
  };

  // ---- Render ---------------------------------------------------------------
  const doctorOptions = useMemo(
    () => Object.entries(doctorMap).map(([id, name]) => ({ id, name })),
    [doctorMap]
  );

  const showCol = (key) => cols.includes(key);
  const optionalKeys = DEFAULT_COLUMNS.filter((c) => c.optional).map((c) => c.key);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="outstanding-page">
      <BillingTabs />

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-ink-soft">Billing · Receivables</div>
          <h1 className="font-display text-4xl sm:text-5xl text-ink mt-1">
            Outstanding, in focus.
          </h1>
          <div className="text-sm text-ink-mid mt-1">
            Oldest-first · aging buckets · one-tap collect &amp; remind.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="outstanding-refresh"
            className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
            title="Refresh data"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="outstanding-export"
                className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] text-ink hover:bg-linen inline-flex items-center gap-1.5"
              >
                <Download className="w-3.5 h-3.5" /> Export
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-[#E8EAE9] w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">CSV</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportRows("page", "csv")}>Current page</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportRows("filtered", "csv")}>Filtered results</DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportRows("all", "csv")}>Entire dataset</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">Excel</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => exportRows("filtered", "xlsx")}><FileSpreadsheet className="w-3.5 h-3.5 mr-2" /> Excel (filtered)</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => exportRows("filtered", "pdf")}><FileText className="w-3.5 h-3.5 mr-2" /> PDF / Print</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Bucket summary cards — clickable filters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {BUCKETS.map((b) => {
          const v = rawData?.totals?.buckets?.[b.key] || { count: 0, amount: 0 };
          const active = filters.bucket === b.key;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => {
                const next = active ? "all" : b.key;
                setFilters((f) => ({ ...f, bucket: next }));
                setDraft((f) => ({ ...f, bucket: next }));
                setChip("all");
              }}
              data-testid={`outstanding-bucket-${b.key}`}
              className={`rounded-2xl border px-3 py-3 text-left transition hover:shadow-sm ${
                active ? "ring-2 ring-pine" : ""
              } ${b.tone}`}
            >
              <div className="text-[10px] uppercase tracking-widest opacity-70">{b.label}</div>
              <div className="font-mono text-lg mt-1">{INR(v.amount)}</div>
              <div className="text-[10px] opacity-80 mt-0.5">
                {v.count} patient{v.count === 1 ? "" : "s"}
              </div>
            </button>
          );
        })}
      </div>

      {/* Sticky filter toolbar */}
      <div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-white/95 backdrop-blur border-y border-[#E8EAE9]" data-testid="outstanding-toolbar">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
            <Input
              value={draft.q}
              onChange={(e) => setDraft({ ...draft, q: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
              placeholder="Search patient by name or phone…"
              data-testid="outstanding-search"
              className="pl-9 h-10"
            />
          </div>

          <FilterSelect
            testid="filter-doctor"
            icon={Stethoscope}
            value={draft.doctorId}
            onChange={(v) => setDraft({ ...draft, doctorId: v })}
            options={[
              { value: "all", label: "All doctors" },
              { value: "unassigned", label: "Unassigned" },
              ...doctorOptions.map((d) => ({ value: d.id, label: d.name })),
            ]}
          />
          <FilterSelect
            testid="filter-bucket"
            icon={Clock}
            value={draft.bucket}
            onChange={(v) => setDraft({ ...draft, bucket: v })}
            options={[{ value: "all", label: "All buckets" }, ...BUCKETS.map((b) => ({ value: b.key, label: b.label }))]}
          />

          <button
            data-testid="filter-more"
            onClick={() => setAdvancedOpen((o) => !o)}
            className={`h-10 px-3 rounded-xl border text-sm inline-flex items-center gap-1.5 transition ${
              advancedOpen ? "bg-linen border-pine/30 text-ink" : "bg-white border-[#E8EAE9] text-ink-mid hover:bg-linen"
            }`}
          >
            <Filter className="w-3.5 h-3.5" /> More filters
            {activeFilterCount > 3 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-clay text-linen">
                +{activeFilterCount - 3}
              </span>
            )}
          </button>

          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="filter-apply"
              onClick={applyFilters}
              className="h-10 px-4 rounded-xl bg-pine text-linen text-sm hover:bg-pine/90 inline-flex items-center gap-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" /> Apply
            </button>
            <button
              data-testid="filter-reset"
              onClick={resetFilters}
              className="h-10 px-3 rounded-xl text-ink-mid hover:bg-linen text-sm inline-flex items-center gap-1.5"
            >
              <X className="w-3.5 h-3.5" /> Reset
            </button>
          </div>
        </div>

        {/* Advanced filter drawer (inline) */}
        {advancedOpen && (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-3 border-t border-[#E8EAE9]" data-testid="filter-advanced">
            <label className="text-xs text-ink-mid">
              Amount ≥
              <Input type="number" min={0} placeholder="0"
                     value={draft.amountMin}
                     onChange={(e) => setDraft({ ...draft, amountMin: e.target.value })}
                     data-testid="filter-amount-min" />
            </label>
            <label className="text-xs text-ink-mid">
              Amount ≤
              <Input type="number" min={0} placeholder="Any"
                     value={draft.amountMax}
                     onChange={(e) => setDraft({ ...draft, amountMax: e.target.value })}
                     data-testid="filter-amount-max" />
            </label>
            <FilterSelectBlock
              testid="filter-reminder"
              label="Reminder status"
              value={draft.reminder}
              onChange={(v) => setDraft({ ...draft, reminder: v })}
              options={[
                { value: "all", label: "Any status" },
                { value: "sent", label: "Sent" },
                { value: "delivered", label: "Delivered" },
                { value: "read", label: "Read" },
                { value: "failed", label: "Failed" },
                { value: "never", label: "Never sent" },
              ]}
            />
            <FilterSelectBlock
              testid="filter-wallet"
              label="Wallet"
              value={draft.wallet}
              onChange={(v) => setDraft({ ...draft, wallet: v })}
              options={[
                { value: "all", label: "Any" },
                { value: "has", label: "Wallet available" },
                { value: "none", label: "No wallet balance" },
              ]}
            />
            <label className="text-xs text-ink-mid">
              Last payment before
              <DateDMYInput
                     value={draft.lastPayBefore}
                     onChange={(e) => setDraft({ ...draft, lastPayBefore: e.target.value })}
                     data-testid="filter-lastpay" />
            </label>
          </div>
        )}

        {/* Quick chips */}
        <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="quick-chips">
          {CHIPS.map((c) => (
            <button
              key={c.key}
              onClick={() => setChip(c.key)}
              data-testid={`chip-${c.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                chip === c.key
                  ? "bg-clay text-linen border-clay"
                  : "bg-white text-ink-mid border-[#E8EAE9] hover:bg-linen"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <KPI icon={TrendingDown} tone="clay"  label="Total outstanding"
             value={INR(rawData?.totals?.outstanding || 0)} testid="outstanding-kpi-total" />
        <KPI icon={Users} tone="pine" label="Patients"
             value={rawData?.totals?.patients || 0} testid="outstanding-kpi-patients" />
        <KPI icon={ArrowUpRight} tone="rose" label=">90 days"
             value={INR((rawData?.totals?.buckets?.["91-180"]?.amount || 0) +
                        (rawData?.totals?.buckets?.["181-365"]?.amount || 0) +
                        (rawData?.totals?.buckets?.["365+"]?.amount || 0))}
             testid="outstanding-kpi-90plus" />
        <KPI icon={Receipt} tone="muted" label="Under 30 days"
             value={INR(rawData?.totals?.buckets?.["0-30"]?.amount || 0)} testid="outstanding-kpi-under30" />
      </div>

      {/* Bulk action bar (only when rows are selected) */}
      {selected.size > 0 && (
        <div className="rounded-2xl border border-pine/30 bg-pine/5 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="bulk-bar">
          <span className="text-sm text-ink">
            <b>{selected.size}</b> selected
          </span>
          <div className="flex items-center gap-1.5 flex-wrap ml-auto">
            <BulkBtn icon={MessageCircle} label="WhatsApp" testid="bulk-whatsapp" onClick={bulkRemind} busy={bulkBusy} />
            <BulkBtn icon={MessageSquare} label="SMS" testid="bulk-sms" onClick={() => notImplemented("Bulk SMS")} />
            <BulkBtn icon={Mail} label="Email" testid="bulk-email" onClick={() => notImplemented("Bulk email")} />
            <BulkBtn icon={Printer} label="Print" testid="bulk-print" onClick={() => window.print()} />
            <BulkBtn icon={Download} label="Export" testid="bulk-export" onClick={() => exportRows("filtered", "csv")} />
            <BulkBtn icon={Bookmark} label="Follow-up" testid="bulk-followup" onClick={() => notImplemented("Mark follow-up")} />
            <button
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear"
              className="text-xs text-ink-soft hover:text-ink ml-1"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-[#E8EAE9] bg-white overflow-hidden" data-testid="outstanding-table-card">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#E8EAE9] text-[10px] uppercase tracking-widest text-ink-soft">
          <span>{sorted.length} result{sorted.length === 1 ? "" : "s"}</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1.5 hover:text-ink" data-testid="columns-menu">
                <Columns3 className="w-3.5 h-3.5" /> Columns
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border-[#E8EAE9]">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest">Optional columns</DropdownMenuLabel>
              {optionalKeys.map((k) => {
                const col = DEFAULT_COLUMNS.find((c) => c.key === k);
                return (
                  <DropdownMenuCheckboxItem
                    key={k}
                    checked={cols.includes(k)}
                    onCheckedChange={(v) => {
                      setCols((prev) => {
                        const next = new Set(prev);
                        if (v) next.add(k); else next.delete(k);
                        // Preserve the default column ordering
                        return DEFAULT_COLUMNS.filter((c) => !c.optional || next.has(c.key)).map((c) => c.key);
                      });
                    }}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-linen/40 text-[10px] uppercase tracking-widest text-ink-soft">
              <tr>
                <th className="px-4 py-3 text-left w-8">
                  <input type="checkbox" checked={allOnPageSelected} onChange={toggleAll}
                         data-testid="select-all" aria-label="Select all on page" />
                </th>
                {DEFAULT_COLUMNS.filter((c) => showCol(c.key)).map((c) => (
                  <th
                    key={c.key}
                    className={`px-4 py-3 select-none whitespace-nowrap ${
                      c.key === "outstanding" || c.key === "wallet" ? "text-right" :
                      c.key === "actions" ? "text-right" : "text-left"
                    } ${c.sortable ? "cursor-pointer hover:text-ink" : ""}`}
                    onClick={() => c.sortable && toggleSort(c.sortKey)}
                  >
                    {c.label} <SortIcon col={c} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={cols.length + 1} className="py-16 text-center text-ink-soft">
                  <Loader2 className="w-5 h-5 animate-spin inline" />
                </td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={cols.length + 1} className="py-16 text-center text-ink-soft text-sm">
                  {activeFilterCount > 0 || chip !== "all"
                    ? "No matches with the current filters."
                    : "Nothing outstanding — you're all caught up!"}
                </td></tr>
              ) : paged.map((r) => (
                <TableRow
                  key={r.patient_id}
                  row={r}
                  showCol={showCol}
                  doctorMap={doctorMap}
                  isSelected={selected.has(r.patient_id)}
                  onToggleSelect={() => toggleOne(r.patient_id)}
                  onOpenDrawer={() => setDrawerRow(r)}
                  onCollect={() => setCollectFor({ id: r.patient_id, name: r.name })}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-[#E8EAE9]">
          {loading ? (
            <div className="py-12 text-center text-ink-soft"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
          ) : paged.length === 0 ? (
            <div className="py-10 text-center text-ink-soft text-sm">No matches with the current filters.</div>
          ) : paged.map((r) => (
            <MobileCard key={r.patient_id} row={r} doctorMap={doctorMap}
                        onOpen={() => setDrawerRow(r)}
                        onCollect={() => setCollectFor({ id: r.patient_id, name: r.name })} />
          ))}
        </div>

        {/* Pagination */}
        {sorted.length > 0 && (
          <Pager
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            total={sorted.length}
            pageStart={pageStart}
            pageCount={paged.length}
            onPage={setPage}
            onPageSize={setPageSize}
          />
        )}
      </div>

      {/* Drawer + dialogs */}
      <OutstandingDrawer
        open={!!drawerRow}
        row={drawerRow}
        doctorMap={doctorMap}
        onClose={() => setDrawerRow(null)}
        onRemind={() => { /* refresh on-demand only */ }}
        onCollect={(r) => setCollectFor({ id: r.patient_id, name: r.name })}
      />
      <CollectPaymentDialog
        open={!!collectFor}
        onOpenChange={(v) => !v && setCollectFor(null)}
        patientId={collectFor?.id}
        patientName={collectFor?.name}
        onSaved={() => { setCollectFor(null); load(); }}
      />
    </div>
  );
}

// ---- Row / Card / helper components ----------------------------------------
function TableRow({ row, showCol, doctorMap, isSelected, onToggleSelect, onOpenDrawer, onCollect }) {
  const b = BUCKETS.find((x) => x.key === row.bucket) || BUCKETS[0];
  const [reminding, setReminding] = useState(false);
  const remind = async (e) => {
    e.stopPropagation();
    setReminding(true);
    try { await api.post(`/outstanding/${row.patient_id}/remind`); toast.success(`Reminder sent to ${row.name}`); }
    catch (err) { toast.error(apiError(err, "Reminder failed")); }
    finally { setReminding(false); }
  };
  const rmStatus = reminderStatus(row);
  const doctorName = doctorMap[row.primary_doctor_id || row.assigned_doctor_id] || "Unassigned";

  return (
    <tr
      data-testid={`outstanding-row-${row.patient_id}`}
      onClick={onOpenDrawer}
      className="border-t border-[#F2F0EB] hover:bg-linen/40 transition cursor-pointer"
    >
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={isSelected} onChange={onToggleSelect}
               data-testid={`select-${row.patient_id}`} aria-label={`Select ${row.name}`} />
      </td>
      {showCol("patient") && (
        <td className="px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <PatientAvatar patient={{ id: row.patient_id, name: row.name }} size="md" />
            <div className="min-w-0">
              <div className="font-medium text-ink truncate" data-testid={`outstanding-name-${row.patient_id}`}>
                {row.name}
              </div>
              <div className="text-[11px] text-ink-soft truncate">{row.phone || "—"}</div>
            </div>
          </div>
        </td>
      )}
      {showCol("doctor") && (
        <td className="px-4 py-3">
          <span className={doctorName === "Unassigned" ? "italic text-ink-soft text-xs" : "text-xs text-ink-mid"}>
            {doctorName}
          </span>
        </td>
      )}
      {showCol("outstanding") && (
        <td className="px-4 py-3 text-right">
          <div className="font-mono text-clay">{INR(row.outstanding)}</div>
          <div className="text-[10px] text-ink-soft">
            {(row.invoices || []).length} invoice{(row.invoices || []).length === 1 ? "" : "s"}
          </div>
        </td>
      )}
      {showCol("age") && (
        <td className="px-4 py-3">
          <span className={`text-[10px] px-2 py-1 rounded-full ${b.pillTone}`}>
            {row.days_overdue}d
          </span>
        </td>
      )}
      {showCol("wallet") && (
        <td className="px-4 py-3 text-right">
          {row.wallet_balance > 0 ? (
            <span className="inline-flex items-center gap-1 text-[#4A2148] text-xs">
              <Wallet className="w-3 h-3" /> {INR(row.wallet_balance)}
            </span>
          ) : <span className="text-ink-disabled text-xs">—</span>}
        </td>
      )}
      {showCol("lastpay") && (
        <td className="px-4 py-3">
          {row.last_payment_at ? (
            <div className="text-[11px] text-ink-mid">
              {new Date(row.last_payment_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}
              <div className="text-[10px] text-ink-soft">{INR(row.last_payment_amount)}</div>
            </div>
          ) : (
            <span className="text-ink-disabled text-[11px]">Never</span>
          )}
        </td>
      )}
      {showCol("reminder") && (
        <td className="px-4 py-3">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border ${REMINDER_TONES[rmStatus]}`}>
            {REMINDER_LABEL[rmStatus]}
          </span>
        </td>
      )}
      {showCol("actions") && (
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-end gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); onCollect(); }}
              data-testid={`outstanding-collect-${row.patient_id}`}
              className="text-xs px-3 py-1.5 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-1.5"
              title="Collect payment"
            >
              <Banknote className="w-3.5 h-3.5" /> Collect
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid={`outstanding-more-${row.patient_id}`}
                  className="w-8 h-8 rounded-full grid place-items-center text-ink-mid hover:bg-linen"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-[#E8EAE9] w-48">
                <DropdownMenuItem onClick={onOpenDrawer}>
                  <Sparkles className="w-4 h-4 mr-2 text-ink-mid" /> View details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={remind} disabled={reminding || !row.phone}>
                  {reminding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2 text-[#1DA850]" />}
                  Send WhatsApp
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => window.open(`/app/patients/${row.patient_id}`, "_blank")}
                >
                  Open patient profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.open(`/app/billing?patient_id=${row.patient_id}`, "_blank")}
                >
                  View billing history
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </td>
      )}
    </tr>
  );
}

function MobileCard({ row, doctorMap, onOpen, onCollect }) {
  const b = BUCKETS.find((x) => x.key === row.bucket) || BUCKETS[0];
  const doctorName = doctorMap[row.primary_doctor_id || row.assigned_doctor_id] || "Unassigned";
  return (
    <div className="p-4 hover:bg-linen/40 transition" onClick={onOpen} data-testid={`outstanding-card-${row.patient_id}`}>
      <div className="flex items-start gap-3">
        <PatientAvatar patient={{ id: row.patient_id, name: row.name }} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-medium text-ink truncate">{row.name}</div>
              <div className="text-[11px] text-ink-soft truncate">{doctorName}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-mono text-clay">{INR(row.outstanding)}</div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${b.pillTone}`}>{row.days_overdue}d</span>
            </div>
          </div>
          <div className="mt-2 flex items-center justify-between text-[11px] text-ink-mid">
            <span>{row.phone || "—"}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onCollect(); }}
              className="text-xs px-3 py-1 rounded-full bg-pine text-linen"
            >
              <Banknote className="inline w-3 h-3 mr-1" /> Collect
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, tone = "pine", testid }) {
  const tones = {
    pine:  "bg-white border-[#E8EAE9] text-ink",
    clay:  "bg-[#FDF3ED] border-[#EAC5AE] text-[#8A3F1E]",
    rose:  "bg-rose-50 border-rose-200 text-rose-800",
    muted: "bg-linen border-[#E8EAE9] text-ink-mid",
  };
  return (
    <div className={`rounded-2xl border px-4 py-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-mono text-2xl mt-1">{value}</div>
    </div>
  );
}

function FilterSelect({ testid, icon: Icon, value, onChange, options }) {
  return (
    <div className="h-10 pl-3 pr-2 rounded-xl border border-[#E8EAE9] bg-white flex items-center gap-2 min-w-[150px]">
      {Icon && <Icon className="w-3.5 h-3.5 text-ink-mid" />}
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-sm focus:outline-none pr-1 flex-1 max-w-[180px] cursor-pointer"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function FilterSelectBlock({ testid, label, value, onChange, options }) {
  return (
    <label className="text-xs text-ink-mid">
      {label}
      <div className="mt-1 h-10 px-3 rounded-xl border border-[#E8EAE9] bg-white flex items-center">
        <select
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="bg-transparent text-sm focus:outline-none w-full cursor-pointer"
        >
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </label>
  );
}

function BulkBtn({ icon: Icon, label, onClick, busy, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testid}
      className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#E8EAE9] hover:bg-linen text-ink-mid inline-flex items-center gap-1.5 disabled:opacity-50"
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

function Pager({ page, totalPages, pageSize, total, pageStart, pageCount, onPage, onPageSize }) {
  const numbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, page, page - 1, page + 1, page - 2, page + 2]);
    const clean = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < clean.length; i++) {
      if (i > 0 && clean[i] - clean[i - 1] > 1) out.push("…");
      out.push(clean[i]);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, totalPages]);

  const btn = "w-8 h-8 rounded-lg text-xs grid place-items-center border border-[#E8EAE9] bg-white hover:bg-linen text-ink transition";
  return (
    <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-[#E8EAE9]"
         data-testid="outstanding-pagination">
      <div className="text-xs text-ink-soft">
        Showing <span className="text-ink">{pageStart + 1}</span>
        {"–"}
        <span className="text-ink">{pageStart + pageCount}</span>
        {" of "}
        <span className="text-ink">{total.toLocaleString("en-IN")}</span> patients
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-center">
        <div className="flex items-center gap-1 text-xs text-ink-soft">
          <span>Rows:</span>
          <select
            data-testid="rows-per-page"
            value={pageSize}
            onChange={(e) => { onPageSize(Number(e.target.value)); onPage(1); }}
            className="text-xs border border-[#E8EAE9] rounded-lg px-2 py-1 bg-white cursor-pointer"
          >
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button data-testid="pager-prev" disabled={page === 1}
                onClick={() => onPage(page - 1)}
                className={`${btn} disabled:opacity-40 disabled:cursor-not-allowed`}
                aria-label="Previous page">
          <ChevronLeft className="w-3.5 h-3.5" />
        </button>
        {numbers.map((n, i) =>
          n === "…" ? (
            <span key={`e-${i}`} className="w-8 h-8 grid place-items-center text-xs text-ink-soft">…</span>
          ) : (
            <button
              key={n}
              data-testid={`pager-${n}`}
              onClick={() => onPage(n)}
              className={`${btn} ${n === page ? "!bg-clay !text-linen !border-clay" : ""}`}
              aria-current={n === page ? "page" : undefined}
            >
              {n}
            </button>
          )
        )}
        <button data-testid="pager-next" disabled={page === totalPages}
                onClick={() => onPage(page + 1)}
                className={`${btn} disabled:opacity-40 disabled:cursor-not-allowed`}
                aria-label="Next page">
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
